import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Popover, Box, Typography, Checkbox, Button, CircularProgress, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GridViewIcon from '@mui/icons-material/GridView';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { colors } from '../../themes/typography';
import { shortName } from '../../utils/productShortName';
import { REPLACEMENT_TEST_WEEKS, startsTooSoonForErp } from '../../constants/experiments';
import { fetchCandidateSuggestionBatch } from '../../pages/ExperimentManagement/services/experimentManagementApi';
import { createBulkReplacement } from '../../pages/ExperimentManagement/services/bulkActionsApi';
import PlanogramSidePanel from './PlanogramSidePanel';
import { MachineQuadrant, alternativeOptions, AlternativeMeta } from '../shared/ReplacementAlternatives';
import { IncomingProductAutocomplete, ExperimentStartFields } from '../shared/IncomingProductPicker';

const PAGE = 15; // machines shown per step; their suggestions come in one batch call
const ROWS_WIDTH = 560; // the machine rows; the paper adds its padding (12px a side) and border (1px a side)
// checkbox · machine · units last month · units this month · planogram · product going out → incoming product · %
const GRID = '20px 58px 40px 40px 22px minmax(0, 0.75fr) minmax(0, 1fr) 58px';

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Requests per opened product, shared between a hover prefetch and the popover so the click reuses
// what is already loading. Short-lived: it only bridges hover → click.
const PREFETCH_TTL_MS = 60_000;
const prefetched = new Map();

// The machine's open experiment on this product when it can't take a new one: accepted/running, or
// any other kind than a replacement. A pending replacement is taken over by the next creation instead.
const lockedBy = (m) => (
  m.experiment && (m.experiment.status !== 'pending' || m.experiment.experimentType !== 'reemplazo_producto') ? m.experiment : null
);

const NO_SUGGESTIONS = { suggestions: {}, volumeSuggestions: {}, quadrants: {} };

// A machine's suggestions as autocomplete options: "más margen" (the recommendation first), "más volumen".
const optionsOf = (s) => alternativeOptions(s).map((o) => ({ ...o, id: o.productId, name: o.productName }));

const keyOf = (args) => [args.clientId, args.scope, args.scopeId, args.productId, args.belowMinUnits ?? ''].join('|');

// The machines carrying the product (live planogram); with a minimum, only those under it last month.
const fetchMachines = ({ clientId, scope, scopeId, productId, belowMinUnits }) => {
  const token = localStorage.getItem('access_token');
  const min = belowMinUnits ? `&belowMinUnits=${belowMinUnits}` : '';
  return axios.get(
    `${API_BASE_URL}/metrics/${scope}/${encodeURIComponent(scopeId)}/products/${productId}/machines?clientId=${encodeURIComponent(clientId)}${min}`,
    { headers: createAuthHeader(token) }
  ).then(({ data }) => ({
    list: (data.machines || []).filter((m) => !belowMinUnits || m.below),
    lastStart: data.lastStart,
    currentStart: data.currentStart,
  }));
};

/** Starts (or reuses) loading a product's machines and the suggestions of the first ones. */
export const prefetchProductMachines = (args) => {
  const key = keyOf(args);
  const hit = prefetched.get(key);
  if (hit && Date.now() - hit.at < PREFETCH_TTL_MS) return hit;
  const machines = fetchMachines(args);
  const firstIds = machines.then(({ list }) => list.slice(0, PAGE).filter((m) => !lockedBy(m)).map((m) => m.dStoreId)).catch(() => []);
  const firstSuggestions = firstIds.then((ids) => (ids.length ? fetchCandidateSuggestionBatch(args.clientId, args.productId, ids) : NO_SUGGESTIONS));
  const entry = { key, at: Date.now(), machines, firstIds, firstSuggestions };
  machines.catch(() => prefetched.delete(key));
  prefetched.set(key, entry);
  return entry;
};

// Same default as a new pending experiment in CreateExperimentForm: 1st of next month — the one after when that
// 1st is closer than the ERP allows.
const defaultStart = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1, 1);
  d.setHours(0, 0, 0, 0);
  if (startsTooSoonForErp(d)) d.setMonth(d.getMonth() + 1, 1);
  return d;
};

/**
 * Small popover at a Top Productos product: the machines of the current scope that carry it — with a
 * minimum (`belowMinUnits`), only those that sold less than that last month — each with its units last
 * month and this month, what would come in instead (the orchestrator's suggestion, switchable to its
 * other options by margin or by volume, with the machine's quadrant as context) and the expected gain.
 * A row opens the machine's planogram on the side. Pick dates and the checked ones become accepted
 * replacement experiments: one click for all the machines.
 *
 * The same popover tries a swap that paid off in more machines ("Probar en N máquinas"): `machineIds` keeps only
 * those machines (in that order), `incoming` is what comes in by default instead of the recommendation,
 * `initialLanes` the lanes each machine gives up to start with, `title` and
 * `subtitle` replace the header, `isAi` tags what it creates and `onCreated` runs once something was created.
 * `opensLeft` opens it up and to the left of its anchor (a link at the right of a table), its bottom right corner on
 * the anchor; a planogram still opens to the right, and the rows stay where they are.
 */
const ProductMachinesPopover = ({
  anchorEl, onClose, clientId, scope, scopeId, product, belowMinUnits, allProducts,
  machineIds, incoming, initialLanes, title, subtitle, isAi = false, onCreated, opensLeft = false,
}) => {
  const { t, i18n } = useTranslation();
  const locale = { es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES';
  const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : '—');
  const [machines, setMachines] = useState(null);
  const [months, setMonths] = useState(null); // { last, current } short month names
  const [openPlanogram, setOpenPlanogram] = useState(null); // dStoreId whose planogram is open on the side
  // The popover sits above the product, so when its content grows it must move up to stay on screen.
  const popoverActions = useRef(null);
  const reposition = () => requestAnimationFrame(() => popoverActions.current?.updatePosition());
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState({}); // dStoreId -> { margin, volume, quadrant } (absent = loading)
  const [choice, setChoice] = useState({});           // dStoreId -> the chosen option ({ id, name, group, …figures })
  const [lanePicks, setLanePicks] = useState(initialLanes ?? {}); // dStoreId -> the product's lanes it gives up (absent = all)
  const [pickedLanes, setPickedLanes] = useState({}); // dStoreId -> the planogram lane its choice was clicked on
  const [checked, setChecked] = useState(new Set());
  const [results, setResults] = useState({});         // dStoreId -> 'created' | skip reason
  const [creating, setCreating] = useState(false);
  const [visible, setVisible] = useState(PAGE);
  const [startDate, setStartDate] = useState(defaultStart);
  const [weeks, setWeeks] = useState(REPLACEMENT_TEST_WEEKS);
  const requested = useRef(new Set());
  // The rows' height: the popover is held by their bottom, so a taller planogram beside them grows it downwards
  const rowsRef = useRef(null);
  const [rowsHeight, setRowsHeight] = useState(0);
  useEffect(() => {
    if (!rowsRef.current) return undefined;
    const observer = new ResizeObserver(() => setRowsHeight(Math.round(rowsRef.current?.offsetHeight || 0)));
    observer.observe(rowsRef.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => { reposition(); }, [machines, openPlanogram, visible, rowsHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  // By default each machine takes `incoming`, else the recommendation: the first "más margen" option.
  const applySuggestions = (ids, batch) => {
    const next = {};
    const picks = {};
    ids.forEach((id) => {
      next[id] = {
        margin: batch.suggestions[id] || [],
        volume: batch.volumeSuggestions[id] || [],
        quadrant: batch.quadrants[id] ?? null,
      };
      const options = optionsOf(next[id]);
      const pick = incoming
        ? options.find((o) => o.id === incoming.id) ?? { ...incoming, group: 'all', key: `all-${incoming.id}` }
        : options[0];
      if (pick) picks[id] = pick;
    });
    setSuggestions((prev) => ({ ...prev, ...next }));
    setChoice((prev) => ({ ...prev, ...picks }));
    setChecked((prev) => new Set([...prev, ...Object.keys(picks).map(Number)]));
  };

  // The machines and the first suggestions, usually already on their way from the hover prefetch.
  useEffect(() => {
    let cancelled = false;
    const entry = prefetchProductMachines({ clientId, scope, scopeId, productId: product.id, belowMinUnits });
    entry.machines
      .then(({ list, lastStart, currentStart }) => {
        if (cancelled) return;
        setMachines(machineIds ? machineIds.map((id) => list.find((m) => m.dStoreId === id)).filter(Boolean) : list);
        const name = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(locale, { month: 'short' }).replace('.', '');
        setMonths({ last: name(lastStart), current: name(currentStart) });
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    entry.firstIds.then((ids) => {
      ids.forEach((id) => requested.current.add(id));
      entry.firstSuggestions.then((batch) => { if (!cancelled) applySuggestions(ids, batch); });
    });
    return () => { cancelled = true; };
  }, [clientId, scope, scopeId, product.id, belowMinUnits]); // eslint-disable-line react-hooks/exhaustive-deps

  // The shown machines the first batch did not cover ("Ver más", or machines picked by `machineIds`): one more call.
  useEffect(() => {
    if (!machines) return;
    const ids = machines.slice(0, visible).filter((m) => !lockedBy(m)).map((m) => m.dStoreId).filter((id) => !requested.current.has(id));
    if (ids.length === 0) return;
    ids.forEach((id) => requested.current.add(id));
    fetchCandidateSuggestionBatch(clientId, product.id, ids).then((batch) => applySuggestions(ids, batch));
  }, [machines, visible, clientId, product.id]); // eslint-disable-line react-hooks/exhaustive-deps


  const ready = useMemo(
    () => (machines || []).filter((m) => checked.has(m.dStoreId) && choice[m.dStoreId] && !results[m.dStoreId]),
    [machines, checked, choice, results]
  );
  const endDate = useMemo(() => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + weeks * 7);
    return d;
  }, [startDate, weeks]);

  const toggle = (id) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const choose = (id, v) => {
    setChoice((prev) => {
      const next = { ...prev };
      if (v) next[id] = v;
      else delete next[id];
      return next;
    });
    setChecked((prev) => {
      const next = new Set(prev);
      if (v) next.add(id); else next.delete(id);
      return next;
    });
  };

  // A lane of the product clicked on the planogram goes out of the change or back in; the last one always stays.
  const toggleLane = (id, selectionId, allLaneIds) => setLanePicks((prev) => {
    const current = prev[id] ?? allLaneIds;
    const next = current.includes(selectionId) ? current.filter((s) => s !== selectionId) : [...current, selectionId];
    if (next.length === 0) return prev;
    const { [id]: _all, ...rest } = prev;
    return next.length === allLaneIds.length ? rest : { ...rest, [id]: next };
  });

  const create = async () => {
    setCreating(true);
    // One bulk call per incoming product: the endpoint takes one replacement for many machines.
    const groups = new Map();
    ready.forEach((m) => {
      const inId = choice[m.dStoreId].id;
      groups.set(inId, [...(groups.get(inId) || []), m.dStoreId]);
    });
    const next = {};
    for (const [replacementProductId, machineIds] of groups) {
      try {
        const res = await createBulkReplacement(clientId, {
          productId: product.id, replacementProductId, machineIds, dateFrom: fmtDate(startDate), dateTo: fmtDate(endDate),
          status: 'accepted',
          // Every lane of the product, or the ones left marked on its planogram
          allLanes: true,
          selectionIdsByMachine: Object.fromEntries(machineIds.filter((id) => lanePicks[id]).map((id) => [id, lanePicks[id]])),
          // A pending replacement of this product in the machine takes this choice, dates and status.
          updatePending: true,
          isAi,
        });
        // Every machine sent is skipped (with its reason), has its pending experiment updated, or is created.
        const skipped = new Map((res.skipped || []).map((s) => [Number(s.dStoreId), s.reason]));
        const updated = new Set((res.updatedExperiments || []).map((u) => Number(u.dStoreId)));
        machineIds.forEach((id) => { next[id] = skipped.get(id) ?? (updated.has(id) ? 'updated' : 'created'); });
      } catch (e) {
        machineIds.forEach((id) => { next[id] = 'error'; });
      }
    }
    setResults((prev) => ({ ...prev, ...next }));
    setCreating(false);
    prefetched.delete(keyOf({ clientId, scope, scopeId, productId: product.id, belowMinUnits }));
    if (Object.values(next).some((r) => r === 'created' || r === 'updated')) onCreated?.();
    // All went through: done. Otherwise stay open so the skipped machines show why.
    if (Object.values(next).every((r) => r === 'created' || r === 'updated')) onClose();
  };

  const remaining = (machines?.length || 0) - visible;

  const row = (m) => {
    const suggestion = suggestions[m.dStoreId];
    const picked = choice[m.dStoreId];
    const result = results[m.dStoreId];
    const alternatives = suggestion ? optionsOf(suggestion) : [];
    const planogramOpen = openPlanogram === m.dStoreId;
    const lock = lockedBy(m);
    const pendingExp = !lock && !result ? m.experiment : null;
    return (
      <Box key={m.dStoreId}>
      <Box sx={{
        display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', gap: 0.75, py: 0.5, opacity: lock ? 0.55 : 1,
        borderRadius: '4px', backgroundColor: planogramOpen ? '#eef2ff' : 'transparent',
      }}>
        <Checkbox
          size="small"
          sx={{ p: 0 }}
          checked={!lock && checked.has(m.dStoreId)}
          disabled={!!lock || !!result || creating || !picked}
          onChange={() => toggle(m.dStoreId)}
        />
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: colors.text.primary }} title={m.alias || m.storeId}>{m.storeId}</Typography>
        <Typography sx={{ fontSize: '12px', color: colors.text.primary, textAlign: 'center' }}>{Math.round(m.unitsLastMonth)}</Typography>
        <Typography sx={{ fontSize: '12px', color: colors.text.secondary, textAlign: 'center' }}>{Math.round(m.unitsThisMonth)}</Typography>
        <IconButton size="small" aria-label={t('productMachines_planogram')} onClick={() => setOpenPlanogram(planogramOpen ? null : m.dStoreId)}
          sx={{ p: 0.25, color: planogramOpen ? '#4f46e5' : colors.text.secondary }}>
          <GridViewIcon sx={{ fontSize: 15 }} />
        </IconButton>
        <Typography noWrap sx={{ fontSize: '11.5px', color: colors.text.primary }}>{shortName(product.name)} →</Typography>
        {lock ? (
          // Already decided here: what comes in and from when.
          <Box sx={{ gridColumn: '7 / 9', minWidth: 0 }}>
            <Typography noWrap title={lock.newProductName} sx={{ fontSize: '11px', color: colors.text.primary }}>
              {lock.newProductName}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: colors.text.secondary }}>
              {t('productMachines_existing', { date: fmtDay(lock.dateFrom) })}
            </Typography>
          </Box>
        ) : suggestion === undefined ? (
          <CircularProgress size={14} />
        ) : (
          <IncomingProductAutocomplete
            value={picked}
            alternatives={alternatives}
            allProducts={allProducts}
            excludeId={product.id}
            disabled={!!result || creating}
            placeholder={suggestion.margin.length ? t('machines_searchProduct') : t('productMachines_noCandidate')}
            onChange={(v) => { choose(m.dStoreId, v); setPickedLanes(({ [m.dStoreId]: _lane, ...rest }) => rest); }}
          />
        )}
        {!lock && <Box sx={{ textAlign: 'right' }}>
          {result === 'created' || result === 'updated' ? (
            <MuiTooltip title={t(`productMachines_${result}`)}><CheckCircleIcon sx={{ fontSize: 16, color: '#15803d' }} /></MuiTooltip>
          ) : result ? (
            <MuiTooltip title={t(`bulkSkipReason_${result}`, result)}>
              <Typography sx={{ fontSize: '10px', color: '#b45309', cursor: 'help' }}>{t('productMachines_skipped')}</Typography>
            </MuiTooltip>
          ) : picked && picked.group !== 'all' && <AlternativeMeta option={picked} />}
        </Box>}
        {/* Context for the choice: the machine's quadrant, the lanes left marked, and the pending experiment this would update. */}
        {!lock && (suggestion?.quadrant || pendingExp || lanePicks[m.dStoreId]) && (
          <Box sx={{ gridColumn: '2 / 9', mt: -0.5, display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
            <MachineQuadrant quadrant={suggestion?.quadrant} tooltip={false} sx={{ fontSize: '10px', whiteSpace: 'nowrap' }} />
            {lanePicks[m.dStoreId] && (
              <Typography noWrap sx={{ fontSize: '10px', color: '#4f46e5', fontWeight: 600 }}>
                {t('productMachines_lanes', { lanes: lanePicks[m.dStoreId].join(' · ') })}
              </Typography>
            )}
            {pendingExp && (
              <Typography noWrap sx={{ fontSize: '10px', color: colors.text.secondary, minWidth: 0 }}>
                {t('productMachines_pendingNote', { product: pendingExp.newProductName, date: fmtDay(pendingExp.dateFrom) })}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      </Box>
    );
  };

  // The open machine's planogram, on the side so the rows stay in view.
  const sidePlanogram = () => {
    const m = (machines || []).find((x) => x.dStoreId === openPlanogram);
    if (!m) return null;
    const suggestion = suggestions[m.dStoreId];
    const alternatives = suggestion ? optionsOf(suggestion) : [];
    const editable = !results[m.dStoreId] && !creating && !lockedBy(m);
    return (
      <PlanogramSidePanel
        title={t('productMachines_planogramOf', { machine: m.storeId })}
        clientId={clientId}
        storeId={m.dStoreId}
        highlightProductId={product.id}
        pickedLaneId={pickedLanes[m.dStoreId]}
        chosenLaneIds={lanePicks[m.dStoreId] ?? null}
        onReady={reposition}
        onToggleLane={editable ? (selectionId, all) => toggleLane(m.dStoreId, selectionId, all) : undefined}
        // A lane's product becomes this machine's choice — as one of the options when it is one, so it keeps its
        // figure — and only that lane is marked; the marked lane clicked again is no longer chosen.
        onPick={editable ? (p) => {
          const again = pickedLanes[m.dStoreId] === p.selectionId;
          choose(m.dStoreId, again ? null : alternatives.find((a) => a.id === p.id) ?? { ...p, group: 'all' });
          setPickedLanes(({ [m.dStoreId]: _lane, ...rest }) => (again ? rest : { ...rest, [m.dStoreId]: p.selectionId }));
        } : undefined}
      />
    );
  };

  const side = sidePlanogram();

  return (
    <Popover
      open
      action={popoverActions}
      anchorEl={anchorEl}
      onClose={onClose}
      // Opens above the product, so the rows below it stay visible: from its left edge, or — `opensLeft` — with the
      // rows' bottom right corner on it. It is held by the rows' bottom left (or right) corner, so a planogram grows it
      // to the right and, when taller than the rows, downwards past the buttons: the rows never move.
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: rowsHeight ? rowsHeight + 13 : 'bottom', horizontal: opensLeft ? ROWS_WIDTH + 26 : 'left' }}
      slotProps={{ paper: { sx: { display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5, borderRadius: '8px', mt: -0.5, border: `1px solid ${colors.border.light}` } } }}
    >
      <Box ref={rowsRef} sx={{ width: ROWS_WIDTH, flexShrink: 0 }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: colors.text.primary, mb: subtitle ? 0.25 : 1 }}>
        {title ?? t('productMachines_title', { product: product.name })}
      </Typography>
      {subtitle && <Typography sx={{ fontSize: '11px', color: colors.text.secondary, mb: 1 }}>{subtitle}</Typography>}

      {machines?.length > 0 && months && (
        <Box sx={{
          display: 'grid', gridTemplateColumns: GRID, alignItems: 'end', gap: 0.75, pb: 0.5, pr: 0.5,
          borderBottom: `1px solid ${colors.border.light}`,
          '& .MuiTypography-root': { fontSize: '10px', lineHeight: 1.15, color: colors.text.secondary, whiteSpace: 'nowrap' },
        }}>
          <span />
          <Typography>{t('productMachines_colMachine')}</Typography>
          <Typography sx={{ textAlign: 'center' }}>{t('productMachines_colUnits', { month: months.last })}</Typography>
          <Typography sx={{ textAlign: 'center' }}>{t('productMachines_colUnits', { month: months.current })}</Typography>
          <span />
          <Typography>{t('laneReplace_colOut')}</Typography>
          <Typography>{t('productMachines_colIn')}</Typography>
          <span />
        </Box>
      )}

      <Box sx={{ maxHeight: 300, overflowY: 'auto', pr: 0.5 }}>
        {error ? (
          <Typography sx={{ fontSize: '11px', color: 'error.main' }}>{error}</Typography>
        ) : !machines ? (
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={18} /></Box>
        ) : machines.length === 0 ? (
          <Typography sx={{ fontSize: '11px', color: colors.text.secondary }}>{t('productMachines_empty')}</Typography>
        ) : (
          machines.slice(0, visible).map(row)
        )}
        {remaining > 0 && (
          <Button size="small" onClick={() => setVisible((v) => v + PAGE)} sx={{ textTransform: 'none', fontSize: '11px', px: 0 }}>
            {t('productMachines_showMore', { count: remaining })}
          </Button>
        )}
      </Box>

      <ExperimentStartFields
        startDate={startDate}
        onStartDateChange={setStartDate}
        weeks={weeks}
        onWeeksChange={setWeeks}
        endDate={endDate}
        locale={locale}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 1.25 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: '12px', color: colors.text.secondary }}>
          {t('cancel')}
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={ready.length === 0 || creating || startsTooSoonForErp(startDate)}
          onClick={create}
          startIcon={creating ? <CircularProgress size={12} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontSize: '12px', backgroundColor: '#2f2a3d', '&:hover': { backgroundColor: '#1f1b2b' } }}
        >
          {t('productMachines_create', { count: ready.length })}
        </Button>
      </Box>
      </Box>
      {side && (
        <Box sx={{ width: 380, flexShrink: 0, pl: 1.5, borderLeft: `1px solid ${colors.border.light}` }}>{side}</Box>
      )}
    </Popover>
  );
};

export default ProductMachinesPopover;
